import React, { useState, useEffect, useCallback, useRef, createContext, useContext } from 'react';
import { Outlet, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  getUserProfile,
  getUserEventCountWithShared,
  getUserPlayerProfileCountWithShared,
  getPlayerProfilesByUser,
  getUserPlayerProfilesWithShared,
  getCoachByPlayerProfile,
  updatePlayerProfile,
} from '../../services/firestoreService';
import DashboardSidebar from '../../components/dashboard/DashboardSidebar';
import DashboardTopBar from '../../components/dashboard/DashboardTopBar';

// Shared dashboard context — consumed by all child pages
export const DashboardContext = createContext(null);

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within DashboardShell');
  }
  return context;
};

const DashboardShell = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile: authUserProfile, logout } = useAuth();

  // --- Account-level data (from TestDashboard) ---
  const [userProfile, setUserProfile] = useState(null);
  const [eventCount, setEventCount] = useState(0);
  const [playerProfileCount, setPlayerProfileCount] = useState(0);
  const [coachData, setCoachData] = useState(null);
  const [accountLoading, setAccountLoading] = useState(true);

  // --- Profile editing data (from DashboardLayout) ---
  const [playerProfiles, setPlayerProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [formData, setFormData] = useState({});
  const [toggles, setToggles] = useState({});
  const [copiedUrl, setCopiedUrl] = useState(false);

  // --- UI state ---
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'dirty' | 'saving' | 'saved'

  // Derived
  const primaryUsername = playerProfiles[0]?.username || null;
  const hasCoachingSetup = !!coachData && coachData.status !== 'library-only';
  const isAdmin = userProfile?.admin === true;
  const isPremium = userProfile?.playerPremium || false;

  // Track whether account-level data has loaded at least once
  const [accountDataLoaded, setAccountDataLoaded] = useState(false);

  // Load account-level data
  useEffect(() => {
    const loadAccountData = async () => {
      if (!currentUser) return;

      try {
        const [profile, events, profileCount] = await Promise.all([
          getUserProfile(currentUser.uid),
          getUserEventCountWithShared(currentUser.uid),
          getUserPlayerProfileCountWithShared(currentUser.uid),
        ]);

        setUserProfile(profile);
        setEventCount(events);
        setPlayerProfileCount(profileCount);
      } catch (error) {
        console.error('Error loading account data:', error);
      } finally {
        setAccountDataLoaded(true);
      }
    };

    loadAccountData();
  }, [currentUser]);

  // Load player profiles (for editing context + profile selector)
  // Depends on userProfile (not authUserProfile) so the admin flag is reliably available
  useEffect(() => {
    const loadProfiles = async () => {
      if (!currentUser) return;
      if (!userProfile) {
        // New account — user doc may not exist yet. Only stop loading once
        // account data has been fetched so playerProfileCount is accurate.
        if (accountDataLoaded) {
          setAccountLoading(false);
        }
        return;
      }

      try {
        const profiles = await getUserPlayerProfilesWithShared(
          currentUser.uid,
          userProfile?.admin === true
        );
        setPlayerProfiles(profiles || []);

        if (profiles && profiles.length > 0) {
          // Check URL param for profile pre-selection (e.g. ?profile=chesswebsite)
          // Only allowed for admin users, and only for profiles with adminCanAccess
          const requestedProfileParam = new URLSearchParams(window.location.search).get('profile');
          let profile = profiles[0];
          if (requestedProfileParam && userProfile?.admin === true) {
            const match = profiles.find(p =>
              (p.id === requestedProfileParam || p.username === requestedProfileParam) && p.adminCanAccess === true
            );
            if (match) profile = match;
          }
          setSelectedProfile(profile);
          setFormData(profile);
          setToggles(profile.toggles || {});

          // Check coaching setup
          const coach = await getCoachByPlayerProfile(profile.id);
          setCoachData(coach);
        }
      } catch (error) {
        console.error('Error loading profiles:', error);
      } finally {
        setAccountLoading(false);
      }
    };

    loadProfiles();
  }, [currentUser, userProfile, accountDataLoaded]);

  // Profile change handler (for multi-profile users)
  const handleProfileChange = async (profileId) => {
    const profile = playerProfiles.find(p => p.id === profileId);
    if (profile) {
      setSelectedProfile(profile);
      setFormData(profile);
      setToggles(profile.toggles || {});

      // Clear coaching data immediately so hasCoachingSetup reflects the new profile
      // before the async fetch completes (prevents showing stale coaching admin)
      setCoachData(null);

      // Reload coaching data for the new profile
      try {
        const coach = await getCoachByPlayerProfile(profile.id);
        setCoachData(coach);
      } catch (error) {
        console.error('Error loading coach data for profile:', error);
        setCoachData(null);
      }
    }
  };

  // Form handlers (from DashboardLayout)
  const handleInputChange = (field, value) => {
    setSaveStatus('dirty');
    setFormData(prev => {
      if (field.includes('.')) {
        const parts = field.split('.');
        const newData = { ...prev };
        let current = newData;
        for (let i = 0; i < parts.length - 1; i++) {
          current[parts[i]] = { ...current[parts[i]] };
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = value;
        return newData;
      }
      return { ...prev, [field]: value };
    });
  };

  const handleToggleChange = (field, value) => {
    setSaveStatus('dirty');
    setToggles(prev => ({ ...prev, [field]: value }));
  };

  // Refs keep the latest data available without changing saveProfile's identity.
  const formDataRef = useRef(formData);
  const togglesRef = useRef(toggles);
  const profileIdRef = useRef(selectedProfile?.id);
  const hasLoadedRef = useRef(false);
  useEffect(() => { formDataRef.current = formData; }, [formData]);
  useEffect(() => { togglesRef.current = toggles; }, [toggles]);
  useEffect(() => { profileIdRef.current = selectedProfile?.id; }, [selectedProfile?.id]);

  // Mark loaded once profiles are set so we don't auto-save the initial load
  useEffect(() => {
    if (selectedProfile?.id) {
      const timer = setTimeout(() => { hasLoadedRef.current = true; }, 500);
      return () => clearTimeout(timer);
    }
  }, [selectedProfile?.id]);

  const saveProfile = useCallback(async () => {
    if (!profileIdRef.current) return;
    if (saveStatusRef.current !== 'dirty') return; // Nothing changed — skip write
    setSaveStatus('saving');
    try {
      await updatePlayerProfile(profileIdRef.current, {
        ...formDataRef.current,
        toggles: togglesRef.current,
      });
      setSaveStatus('saved');
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveStatus('dirty'); // Failed — still unsaved
    }
  }, []);

  // Track saveStatus in a ref so the debounce check doesn't reset the timer
  const saveStatusRef = useRef(saveStatus);
  useEffect(() => { saveStatusRef.current = saveStatus; }, [saveStatus]);

  // Debounced auto-save: writes to Firestore 1.5s after the last change.
  // Skips the initial load so we don't write back what we just read.
  // Only fires when there are actual unsaved changes (dirty).
  useEffect(() => {
    if (!hasLoadedRef.current || !selectedProfile?.id) return;
    const timer = setTimeout(() => {
      if (saveStatusRef.current === 'dirty') {
        saveProfile();
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [formData, toggles, selectedProfile?.id, saveProfile]);

  // Auto-clear "saved" indicator after 3 seconds
  useEffect(() => {
    if (saveStatus === 'saved') {
      const timer = setTimeout(() => setSaveStatus('idle'), 3000);
      return () => clearTimeout(timer);
    }
  }, [saveStatus]);

  // Dirty-tracking wrappers — context consumers use these instead of raw setters
  // so any direct setFormData/setToggles call from child pages marks dirty.
  const trackSetFormData = useCallback((updater) => {
    setSaveStatus('dirty');
    setFormData(updater);
  }, []);

  const trackSetToggles = useCallback((updater) => {
    setSaveStatus('dirty');
    setToggles(updater);
  }, []);

  const copyProfileUrl = () => {
    if (selectedProfile?.username) {
      const url = `https://${selectedProfile.username}.chessweb.site`;
      navigator.clipboard.writeText(url);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Context value — superset of old DashboardLayout + TestDashboard data
  const contextValue = {
    // Profile editing (from DashboardLayout)
    selectedProfile,
    formData,
    setFormData: trackSetFormData,
    toggles,
    setToggles: trackSetToggles,
    handleInputChange,
    handleToggleChange,
    saveProfile,
    playerProfiles,
    handleProfileChange,
    copyProfileUrl,
    copiedUrl,

    // Save status
    saveStatus,

    // Account data (from TestDashboard)
    userProfile,
    currentUser,
    eventCount,
    playerProfileCount,
    coachData,
    isPremium,
    isAdmin,
    isLoading: accountLoading,
    hasCoachingSetup,
    primaryUsername,
  };

  // Loading state
  if (accountLoading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-chess-green/20 flex items-center justify-center animate-pulse">
            <i className="fas fa-chess-king text-chess-green text-xl"></i>
          </div>
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // New account onboarding
  const isNewAccount =
    playerProfileCount === 0 &&
    eventCount === 0 &&
    userProfile?.tournamentOrganizer !== true;

  if (isNewAccount) {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <DashboardContext.Provider value={contextValue}>
      <div className="h-screen bg-[#1a1a1a] flex overflow-hidden">
        {/* Sidebar */}
        <DashboardSidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isPremium={isPremium}
          primaryUsername={primaryUsername}
          userProfile={userProfile}
          onLogout={handleLogout}
        />

        {/* Main content area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top bar */}
          <DashboardTopBar
            onMenuToggle={() => setSidebarOpen(prev => !prev)}
            userProfile={userProfile}
            playerProfiles={playerProfiles}
            selectedProfile={selectedProfile}
            onProfileChange={handleProfileChange}
            onLogout={handleLogout}
            isAdmin={isAdmin}
            saveStatus={saveStatus}
          />

          {/* Page content */}
          <main className="flex-1 overflow-y-auto p-4">
            <div className="bg-gray-900/50 border border-gray-800 rounded-2xl min-h-full">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </DashboardContext.Provider>
  );
};

export default DashboardShell;
